import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useCenturion } from "../context/CenturionContext";
import { useCompany } from "../context/CompanyContext";
import { toast } from "sonner";
import { Save, Lock, Settings as SettingsIcon } from "lucide-react";

const FIELDS = [
  {
    group: "Identity",
    items: [
      { key: "company_name", label: "Company Name", placeholder: "Acme Inc." },
      { key: "owner_name", label: "Owner / Signatory Name", placeholder: "Jane Doe" },
    ],
  },
  {
    group: "Contact & Address",
    items: [
      { key: "contact_email", label: "Contact Email", placeholder: "you@company.com" },
      { key: "address", label: "Address", placeholder: "123 Main St, City, Province/State" },
      { key: "default_jurisdiction", label: "Governing Jurisdiction", placeholder: "Ontario, Canada" },
    ],
  },
  {
    group: "Billing",
    items: [
      { key: "payment_instructions", label: "Payment Instructions", placeholder: "E-Transfer to you@company.com" },
      { key: "invoice_number_prefix", label: "Invoice Number Prefix", placeholder: "ACME" },
      { key: "default_currency", label: "Default Currency", placeholder: "CAD" },
    ],
  },
  {
    group: "Documents & Branding",
    items: [
      { key: "document_number_prefix", label: "Document Number Prefix", placeholder: "ACME-DOC" },
      { key: "brand_footer", label: "Print Footer Tagline", placeholder: "Generated by Acme Forge" },
    ],
  },
];

const SettingsPage = () => {
  const { unlocked } = useCenturion();
  const { company, loading, save } = useCompany();
  const [form, setForm] = useState(company);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(company);
  }, [company]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await save(form);
      toast.success("Company settings saved");
    } catch (err) {
      console.error(err);
      toast.error("Save failed — check console for details");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-12 animate-pulse">
          <div className="h-6 w-40 bg-white/5 rounded mb-6" />
          <div className="bg-white/5 h-[500px] rounded-lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-2">
            <SettingsIcon size={13} /> Settings
          </div>
          <h1 className="text-[32px] md:text-[36px] font-semibold tracking-tight">Company Profile</h1>
          <p className="text-white/55 mt-3 text-[13px] leading-[1.7] max-w-xl">
            This identity is stamped onto every new invoice and document you issue — name, payment
            instructions, and branding. Editing it never rewrites documents you've already issued;
            they keep the identity that was in effect when they were created.
          </p>
        </div>

        {!unlocked && (
          <div className="glass p-4 mb-6 flex items-center gap-4">
            <Lock size={18} className="text-[#d4a54b]" />
            <div className="flex-1 text-[13px]">
              <span className="text-white/80 font-medium">Issuer mode is locked.</span>{" "}
              <span className="text-white/55">Type the keyword anywhere to enable saving.</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {FIELDS.map((section) => (
            <section key={section.group} className="glass p-6">
              <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-4">{section.group}</h3>
              <div className="space-y-4">
                {section.items.map((f) => (
                  <div key={f.key}>
                    <label className="field-label">{f.label}</label>
                    <input
                      className="field"
                      value={form?.[f.key] || ""}
                      placeholder={f.placeholder}
                      onChange={(e) => setField(f.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="glass p-6">
            <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-4">Legal Disclaimer</h3>
            <p className="text-white/45 text-[12px] mb-3 leading-[1.6]">
              Shown before drafting and printed on every legal/HR document produced by Document Studio.
            </p>
            <textarea
              rows={4}
              className="field"
              value={form?.legal_disclaimer || ""}
              onChange={(e) => setField("legal_disclaimer", e.target.value)}
            />
          </section>

          <div className="glass p-5 sticky bottom-4 z-10 flex items-center justify-end gap-2">
            <button
              type="submit"
              disabled={!unlocked || saving}
              className="btn-primary disabled:opacity-50"
              title={!unlocked ? "Unlock issuer mode (type 'centurion')" : ""}
            >
              {!unlocked ? <Lock size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : "Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default SettingsPage;
